import React, { useEffect, useState } from "react";
import CommonTable from "../../../components/Table/Table";
import { Eye, X } from "lucide-react";
import "./UserList.css";
import {
  getAllBuyers,
  getAllSellers,
  getSellerForVerification,
  approveSeller,
  rejectSeller,
} from "../../../services/Users/User.service";
import RegisterUserModal from "../Adduser/Addusers";
import { toast } from "react-toastify";

const UsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"buyer" | "seller">("buyer");
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const [selectedSeller, setSelectedSeller] = useState<any>(null);
  const [openSellerModal, setOpenSellerModal] = useState(false);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Get token from localStorage or your auth context
  const token = localStorage.getItem('token') || '';

  const loadUsers = async () => {
    try {
      setLoading(true);
      if (activeTab === "buyer") {
        const data = await getAllBuyers();
        setUsers(data);
      } else {
        const data = await getAllSellers();
        setUsers(data);
      }
    } catch (error) {
      console.error("User fetch error", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    setCurrentPage(1);
  }, [activeTab]);

  /* ---------------- SELLER VIEW ---------------- */

  const handleViewSeller = async (id: number) => {
    try {
      setLoading(true);
      const data = await getSellerForVerification(id);
      setSelectedSeller(data);
      setStatus(data.approvalStatus);
      setOpenSellerModal(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load seller details");
    } finally {
      setLoading(false);
    }
  };

  const updateSellerStatus = async () => {
    if (!selectedSeller) return;

    try {
      setLoading(true);
      if (status === "approved") {
        await approveSeller(selectedSeller.id);
        toast.success("Seller Approved Successfully!");
      }

      if (status === "rejected") {
        await rejectSeller(selectedSeller.id, "Rejected by admin");
        toast.success("Seller Rejected!");
      }

      setOpenSellerModal(false);
      loadUsers();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- SEARCH FILTER ---------------- */

  const filteredUsers = users.filter((u) =>
    `${u.firstName} ${u.lastName} ${u.businessName} ${u.email} ${u.mobileNumber}`
      ?.toLowerCase()
      .includes(search.toLowerCase())
  );

  /* ---------------- PAGINATION ---------------- */

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedUsers = filteredUsers
    .slice(startIndex, startIndex + itemsPerPage)
    .map((user, index) => ({
      ...user,
      serial: startIndex + index + 1,
    }));

  /* ---------------- TABLE COLUMNS ---------------- */

  const columns = [
    { key: "serial", label: "S.NO" },

    {
      key: "name",
      label: "Name",
      render: (_: any, row: any) =>
        `${row.firstName || ""} ${row.lastName || ""}`,
    },

    {
      key: "businessName",
      label: "Business Name",
      render: (_: any, row: any) => row.businessName || "—",
    },
    {
      key: "mostlydeal",
      label: "Mostly deal In",
      render: (_: any, row: any) =>
        row.product_information_master?.name || "—",
    },

    {
      key: "contact",
      label: "Contact",
      render: (_: any, row: any) => (
        <div>
          <div>📞 {row.mobileNumber || "—"}</div>
          <div className="muted">{row.email || "—"}</div>
        </div>
      ),
    },
    {
      key: "Address",
      label: "Address",
      render: (_: any, row: any) => (
        <div>
          <div> {row.address || "—"}</div>

        </div>
      ),
    },

    {
      key: "City",
      label: "City",
      render: (_: any, row: any) => (
        <div>
          <div> {row.city || "—"}</div>

        </div>
      ),
    },


    {
      key: "State",
      label: "State",
      render: (_: any, row: any) => (
        <div>
          <div> {row.state || "—"}</div>

        </div>
      ),
    },


    {
      key: "bank",
      label: "Bank Details",
      render: (_: any, row: any) => (
        <div>
          <div>{row.AccountNumber || "—"}</div>
          <div className="muted">{row.IFSCCode || "—"}</div>
        </div>
      ),
    },

    ...(activeTab === "seller"
      ? [
        {
          key: "status",
          label: "Status",
          render: (_: any, row: any) => (
            <span className={`status-badge ${row.approvalStatus}`}>
              {row.approvalStatus ? row.approvalStatus.charAt(0).toUpperCase() + row.approvalStatus.slice(1) : "Pending"}
            </span>
          ),
        },
      ]
      : []),

    {
      key: "role",
      label: "Role",
      render: () => (
        <span className={`role-badge ${activeTab}`}>
          {activeTab === "buyer" ? "Buyer" : "Seller"}
        </span>
      ),
    },
  ];

  return (
    <div className="user-page">
      {/* ---------------- STATS ---------------- */}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Users</div>
          <div className="stat-value">{users.length}</div>
        </div>

        <div className="stat-card blue">
          <div className="stat-label">Buyers</div>
          <div className="stat-value">{activeTab === "buyer" ? users.length : 0}</div>
        </div>

        <div className="stat-card purple">
          <div className="stat-label">Sellers</div>
          <div className="stat-value">{activeTab === "seller" ? users.length : 0}</div>
        </div>

        <div className="stat-card orange">
          <div className="stat-label">Pending</div>
          <div className="stat-value">
            {users.filter((u) => u.approvalStatus === "pending").length}
          </div>
        </div>
      </div>

      {/* ---------------- TABS ---------------- */}

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={activeTab === "buyer" ? "active" : ""}
            onClick={() => setActiveTab("buyer")}
          >
            Users (Buyers)
          </button>

          <button
            className={activeTab === "seller" ? "active" : ""}
            onClick={() => setActiveTab("seller")}
          >
            Sellers
          </button>
        </div>
      </div>

      {/* ---------------- SEARCH & REGISTER ---------------- */}

      <div className="table-toolbar">
        <div className="search-wrapper">
          <input
            className="search-input"
            placeholder="Search by name, business, email or phone..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          {search && (
            <button className="clear-search" onClick={() => setSearch("")}>
              ×
            </button>
          )}
        </div>

        <button className="register-btn" onClick={() => setOpenModal(true)}>
          ➕ Register {activeTab === "buyer" ? "User" : "Seller"}
        </button>
      </div>

      {/* ---------------- TABLE ---------------- */}

      <CommonTable
        columns={columns}
        data={paginatedUsers}
        actions={(row: any) => (
          <button
            className="icon-btn"
            onClick={() => activeTab === "seller" && handleViewSeller(row.id)}
            title="View Details"
          >
            <Eye size={18} />
          </button>
        )}
      />

      {/* ---------------- PAGINATION ---------------- */}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* ---------------- REGISTER MODAL ---------------- */}

      <RegisterUserModal
        open={openModal}
        type={activeTab}
        onClose={() => setOpenModal(false)}
        token={token}
      />

      {/* ---------------- SELLER DETAILS MODAL ---------------- */}

      {openSellerModal && selectedSeller && (
        <div className="modal-overlay" onClick={() => setOpenSellerModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>

            <div className="modal-header">
              <h2>Seller Details</h2>
              <X className="close-icon" onClick={() => setOpenSellerModal(false)} />
            </div>

            <div className="modal-content-scroll">
              {/* Basic Information */}
              <div className="info-section">
                <h3>Basic Information</h3>
                <table className="details-table">
                  <tbody>
                    <tr>
                      <td>Name</td>
                      <td>{selectedSeller.firstName} {selectedSeller.lastName}</td>
                    </tr>
                    <tr>
                      <td>Business Name</td>
                      <td>{selectedSeller.businessName || "—"}</td>
                    </tr>
                    <tr>
                      <td>Mobile Number</td>
                      <td>{selectedSeller.mobileNumber || "—"}</td>
                    </tr>
                    <tr>
                      <td>Business Type</td>
                      <td>{selectedSeller.Type_of_business?.name || "—"}</td>
                    </tr>

                    <tr>
                      <td>Mostly Deal In</td>
                      <td>{selectedSeller.product_information_master?.name || "—"}</td>
                    </tr>

                    <tr>
                      <td>Email</td>
                      <td>{selectedSeller.email || "—"}</td>
                    </tr>
                    <tr>
                      <td>Address</td>
                      <td>{selectedSeller.address || "—"}</td>
                    </tr>
                    <tr>
                      <td>City</td>
                      <td>{selectedSeller.city || "—"}</td>
                    </tr>
                    <tr>
                      <td>State</td>
                      <td>{selectedSeller.state || "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Document Details */}
              <div className="info-section">
                <h3>Document Details</h3>
                <table className="details-table">
                  <tbody>
                    <tr>
                      <td>PAN Number</td>
                      <td>{selectedSeller.panNumber || "—"}</td>
                    </tr>
                    <tr>
                      <td>Aadhaar Number</td>
                      <td>{selectedSeller.aadhaarNumber || "—"}</td>
                    </tr>
                    <tr>
                      <td>GST Number</td>
                      <td>{selectedSeller.gstNumber || "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Bank Details */}
              <div className="info-section">
                <h3>Bank Details</h3>
                <table className="details-table">
                  <tbody>
                    <tr>
                      <td>Bank Name</td>
                      <td>{selectedSeller.bankName || "—"}</td>
                    </tr>
                    <tr>
                      <td>Account Holder</td>
                      <td>{selectedSeller.AccountHolderName || "—"}</td>
                    </tr>
                    <tr>
                      <td>Account Number</td>
                      <td>{selectedSeller.AccountNumber || "—"}</td>
                    </tr>
                    <tr>
                      <td>IFSC Code</td>
                      <td>{selectedSeller.IFSCCode || "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Document Images - Updated to use direct S3 URLs */}
              <div className="info-section">
                <h3>Uploaded Documents</h3>
                <div className="doc-section">
                  <div className="doc-item">
                    <p>PAN Card</p>
                    {selectedSeller.panImage ? (
                      <img
                        src={selectedSeller.panImage}
                        alt="PAN"
                        onClick={() => window.open(selectedSeller.panImage, '_blank')}
                        style={{ cursor: 'pointer', maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = ''; // or a placeholder image
                          e.currentTarget.alt = 'Image not available';
                        }}
                      />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  <div className="doc-item">
                    <p>GST Certificate</p>
                    {selectedSeller.gstCertificateImage ? (
                      <img
                        src={selectedSeller.gstCertificateImage}
                        alt="GST"
                        onClick={() => window.open(selectedSeller.gstCertificateImage, '_blank')}
                        style={{ cursor: 'pointer', maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '';
                          e.currentTarget.alt = 'Image not available';
                        }}
                      />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  <div className="doc-item">
                    <p>Cancel Cheque</p>
                    {selectedSeller.cancelCheckImage ? (
                      <img
                        src={selectedSeller.cancelCheckImage}
                        alt="Cheque"
                        onClick={() => window.open(selectedSeller.cancelCheckImage, '_blank')}
                        style={{ cursor: 'pointer', maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '';
                          e.currentTarget.alt = 'Image not available';
                        }}
                      />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Update */}
              <div className="status-box">
                <label htmlFor="status">Update Status</label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="btn-box">
              <button
                className="close-btnn"
                onClick={() => setOpenSellerModal(false)}
                disabled={loading}
              >
                Close
              </button>
              <button
                className="update-btn"
                onClick={updateSellerStatus}
                disabled={loading || status === selectedSeller.approvalStatus}
              >
                {loading ? "Updating..." : "Update Status"}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;